import type {InsightFormat} from "../../models/common";
import type {InsightUnavailableReasonV4} from "../../models/crm-v4";

export interface InsightStatMetricDefinition {
    id: string;
    title: string;
    hint: string;
    fractionDigits?: number;
    format: InsightFormat;
}

const REASON_MESSAGES: Record<InsightUnavailableReasonV4, string> = {
    moduleNotActive: "Not available without the accommodation module",
    roomCountMissing: "Estimated from the rooms seen in your data — set your property's room count for an exact figure",
    roomCountStale: "Estimated from the rooms seen in your data — your configured room count is lower than the rooms actually occupied",
    notSupportedByPms: "Your property management system does not send a booking date",
    partialData: "Calculated from the booking dates available for only part of the selected period's stays",
    noAncillaryItems: "No eligible non-room folio items were found in the selected period",
    noDataInRange: "No data in the selected period",
};

const UNIT_LABELS: Record<string, string> = {
    nights: "nights",
    days: "days",
    roomNights: "nights",
    stays: "stays",
    years: "years",
    guests: "guests",
};

export function formatStatValue(
    format: InsightFormat,
    value: number | null | undefined,
    fractionDigits?: number,
    currency = "EUR",
    locale?: string,
): string {
    if (value == null) {
        return "—";
    }

    if (format === "PERCENT") {
        return `${value.toLocaleString(locale, {maximumFractionDigits: 1})}%`;
    }

    if (format === "CURRENCY") {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            maximumFractionDigits: fractionDigits ?? 0,
        }).format(value);
    }

    return value.toLocaleString(locale, {
        maximumFractionDigits: fractionDigits ?? 1,
        minimumFractionDigits: fractionDigits ?? 1,
    });
}

export function unitLabel(unit?: string): string | undefined {
    return unit ? UNIT_LABELS[unit] ?? unit : undefined;
}

export function reasonMessage(
    reason?: InsightUnavailableReasonV4,
    coverage?: number,
): string | undefined {
    if (!reason) return undefined;
    if (reason === "partialData" && coverage != null) {
        const formattedCoverage = coverage.toLocaleString(undefined, {maximumFractionDigits: 1});
        return `Calculated from booking dates available for ${formattedCoverage}% of eligible stays`;
    }
    return REASON_MESSAGES[reason];
}

/** How big a change was, with no sign: whatever renders it already shows the direction. */
export function formatDeltaMagnitude(delta: number, kind: "percent" | "points"): string {
    const magnitude = Math.abs(delta).toLocaleString(undefined, {maximumFractionDigits: 1});

    return `${magnitude}${kind === "points" ? "pp" : "%"}`;
}
